import { useState } from 'react';
import { Button, Modal } from '../common';
import { ExportService, type ExportOptions } from '../../services/exportService';
import type { DrawnCard, TarotSpread } from '../../types/tarot';
import { 
  Share2, 
  FileImage, 
  FileText, 
  Link, 
  Copy,
  Settings
} from 'lucide-react';
import toast from 'react-hot-toast';

interface ExportMenuProps {
  reading: {
    question: string;
    cards: DrawnCard[];
    spread: TarotSpread;
    interpretation?: string;
    aiInterpretation?: string;
  };
  readingId?: string;
  elementId?: string; // ID of the element to capture for image export
}

export function ExportMenu({ reading, readingId, elementId = 'reading-content' }: ExportMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [exportOptions, setExportOptions] = useState<ExportOptions>({
    includeCardImages: true,
    includeInterpretation: true,
    format: 'png',
    quality: 0.9
  });

  const handleExportPDF = async () => {
    setIsExporting(true);
    try {
      await ExportService.exportToPDF(reading, exportOptions);
      toast.success('PDF exported successfully!');
      setIsOpen(false);
    } catch (error) {
      console.error('PDF export failed:', error);
      toast.error('Failed to export PDF');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportImage = async () => {
    setIsExporting(true);
    try {
      await ExportService.exportToImage(elementId, exportOptions);
      toast.success('Image exported successfully!');
      setIsOpen(false);
    } catch (error) {
      console.error('Image export failed:', error);
      toast.error('Failed to export image. Make sure the reading is fully loaded.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleShare = async () => {
    try {
      const permalink = readingId 
        ? ExportService.generatePermalink(readingId)
        : ExportService.createReadingPermalink(reading);
      
      await ExportService.shareReading(reading, permalink);
      toast.success('Reading shared!');
    } catch (error) {
      console.error('Share failed:', error);
      toast.error('Failed to share reading');
    }
  };

  const handleCopyPermalink = async () => {
    try {
      const permalink = readingId 
        ? ExportService.generatePermalink(readingId)
        : ExportService.createReadingPermalink(reading);
      
      await navigator.clipboard.writeText(permalink);
      toast.success('Link copied to clipboard!');
    } catch (error) {
      console.error('Copy failed:', error);
      toast.error('Failed to copy link');
    }
  };

  const handleCopyText = async () => {
    try {
      const shareText = ExportService.generateShareText(reading);
      await navigator.clipboard.writeText(shareText);
      toast.success('Reading text copied to clipboard!');
    } catch (error) {
      console.error('Copy failed:', error);
      toast.error('Failed to copy text');
    }
  };

  return (
    <>
      <Button
        variant="ghost"
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-2"
        disabled={isExporting}
      >
        <Share2 className="w-4 h-4" />
        Export & Share
      </Button>

      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Export & Share Reading"
        size="md"
      >
        <div className="space-y-6">
          {/* Quick Actions */}
          <div className="grid grid-cols-2 gap-3">
            <Button
              onClick={handleShare}
              className="flex items-center gap-2 justify-center"
              disabled={isExporting}
            >
              <Share2 className="w-4 h-4" />
              Share
            </Button>
            
            <Button
              onClick={handleCopyPermalink}
              variant="secondary"
              className="flex items-center gap-2 justify-center"
              disabled={isExporting}
            >
              <Link className="w-4 h-4" />
              Copy Link
            </Button>
          </div>

          {/* Export Options */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">Export Options</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowOptions(!showOptions)}
                className="flex items-center gap-1"
              >
                <Settings className="w-4 h-4" />
                Options
              </Button>
            </div>

            {showOptions && (
              <div className="bg-dark-700 p-4 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm text-gray-300">Include card images</label>
                  <input
                    type="checkbox"
                    checked={exportOptions.includeCardImages}
                    onChange={(e) => setExportOptions(prev => ({
                      ...prev,
                      includeCardImages: e.target.checked
                    }))}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm text-gray-300">Include interpretation</label>
                  <input
                    type="checkbox"
                    checked={exportOptions.includeInterpretation}
                    onChange={(e) => setExportOptions(prev => ({
                      ...prev,
                      includeInterpretation: e.target.checked
                    }))}
                    className="rounded"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <label className="text-sm text-gray-300">Image format</label>
                  <select
                    value={exportOptions.format}
                    onChange={(e) => setExportOptions(prev => ({
                      ...prev,
                      format: e.target.value as 'png' | 'jpeg'
                    }))}
                    className="bg-dark-600 text-white rounded px-2 py-1 text-sm"
                  >
                    <option value="png">PNG</option>
                    <option value="jpeg">JPEG</option>
                  </select>
                </div>

                {exportOptions.format === 'jpeg' && (
                  <div className="flex items-center justify-between">
                    <label className="text-sm text-gray-300">Quality</label>
                    <input
                      type="range"
                      min="0.1"
                      max="1"
                      step="0.1"
                      value={exportOptions.quality}
                      onChange={(e) => setExportOptions(prev => ({
                        ...prev,
                        quality: parseFloat(e.target.value)
                      }))}
                      className="w-20"
                    />
                    <span className="text-sm text-gray-400 w-8">
                      {Math.round((exportOptions.quality || 0.9) * 100)}%
                    </span>
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 gap-3">
              <Button
                onClick={handleExportPDF}
                variant="secondary"
                className="flex items-center gap-2 justify-center"
                disabled={isExporting}
              >
                <FileText className="w-4 h-4" />
                {isExporting ? 'Exporting...' : 'Export as PDF'}
              </Button>
              
              <Button
                onClick={handleExportImage}
                variant="secondary"
                className="flex items-center gap-2 justify-center"
                disabled={isExporting}
              >
                <FileImage className="w-4 h-4" />
                {isExporting ? 'Exporting...' : 'Export as Image'}
              </Button>
              
              <Button
                onClick={handleCopyText}
                variant="ghost"
                className="flex items-center gap-2 justify-center"
                disabled={isExporting}
              >
                <Copy className="w-4 h-4" />
                Copy Text
              </Button>
            </div>
          </div>

          {/* Info */}
          <div className="text-xs text-gray-400 bg-dark-700 p-3 rounded">
            <p className="mb-1">
              <strong>Share:</strong> Uses native sharing when available, falls back to clipboard
            </p>
            <p className="mb-1">
              <strong>PDF:</strong> Creates a formatted document with all reading details
            </p>
            <p>
              <strong>Image:</strong> Captures the current reading display as an image
            </p>
          </div>
        </div>
      </Modal>
    </>
  );
}